/**
 * Resource Module - PSP/CRE Tool v3.0
 * 
 * Handles medical resource calculations including:
 * - Blood product requirements (PRBC, FFP, PLT, Cryo, TXA)
 * - Walking Blood Bank donor requirements
 * - Surgical capacity and queue management
 * - Medical supply (Class VIII) consumption
 * 
 * REPLACES HARDCODED VALUES from v3.1:
 * - Line 933: 0.10 cryo requirement → BLOOD_CONSUMPTION_RATES.cryo_percent
 * - Line 1046: 0.6/0.4 DCS ratio → SURGICAL_PROCEDURES.dcs_threshold
 * 
 * @version 4.4
 * @author Dr. Tatar Radu MD
 * 
 * VERSION 4.4 FIXES (February 24, 2026 — Phase 6):
 * - RM-01 FIX: Replaced `dailyCasualties * (5 + 2 + 3)` with explicit field
 *   references: consumptionFactors.MEDICAL_MATERIEL.kg_per_casualty +
 *   PHARMACEUTICALS.kg_per_casualty + SURGICAL_SUPPLIES.kg_per_casualty.
 *   Now references CLASS_VIII_CATEGORIES through the already-validated consumptionFactors
 *   object — no hardcoded values, no change to output when constants match defaults.
 * - RM-02 FIX: Replaced `0.15/0.25/0.30` triage fallback fractions in
 *   calculateMedicalSupplyConsumption() with DEFAULT_CASUALTY_MIX.t1/.t2/.t3.
 *   Added DEFAULT_CASUALTY_MIX to classVIII.js import.
 * - Removed @institution line (APP_RULES compliance).
 *
 * VERSION 4.3 FIXES (January 20, 2026):
 * - BUG-032 FIX: t1Config.mtp_protocol.prbc undefined crash
 *   - Added validateBloodByTriageStructure() and validateSurgeryByTriageStructure()
 *
 * - BUG-035 FIX: DCS_THRESHOLDS.PHASES.PHASE_1 undefined crash
 *   - Added validateDCSStructure() and validateSurgicalFatigueStructure()
 *
 * - BUG-036 FIX: consumptionFactors.MEDICAL_MATERIEL.kg_per_casualty undefined crash
 *   - Added inline validation for CLASS_VIII_CATEGORIES, MDOS_STANDARDS, WALKING_BLOOD_BANK
 *   - Pattern: Check specific property exists before using, else use default
 * 
 * ROOT CAUSE (All bugs above): The `||` fallback pattern:
 *   const X = IMPORTED_CONST || { default: 'value' }
 * Only triggers when left side is null/undefined. If IMPORTED_CONST exists
 * but has DIFFERENT STRUCTURE, the fallback never runs and code crashes later.
 */

// ============================================================================
// IMPORTS
// ============================================================================

// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { resolveInput } from '../assumptionLedger.js';

import {
  BLOOD_PRODUCTS,
  BLOOD_ALERT_LEVELS,
  BLOOD_CONSUMPTION_RATES,
  WALKING_BLOOD_BANK,
  MASSIVE_TRANSFUSION_PROTOCOL,
  getBloodShortageDOWModifier,
  calculateBloodRequirements,
  calculateWBBDonorRequirement
} from '../../constants/bloodProducts.js';

import {
  SURGERY_REQUIREMENTS_BY_TRIAGE,
  SURGICAL_CAPACITY_BY_MTF,
  SURGICAL_FATIGUE_MODEL,
  DCS_PHASES,
  calculateSurgicalCapacity,
  getSurgeonPerformance
} from '../../constants/surgicalCapacity.js';

import {
  TRIAGE_CATEGORIES
} from '../../constants/triageCategories.js';

import {
  MDOS_STANDARDS,
  CLASS_VIII_CATEGORIES,
  CONSUMPTION_RATES,
  DEFAULT_CASUALTY_MIX,
  calculateMDOS
} from '../../constants/classVIII.js';
import { deriveSurgicalTempo } from './surgicalTempoModule.js';
import { buildSurgicalDestinations, regulateSurgicalCases } from './surgicalRegulatingModule.js';

// ============================================================================
// CONSTANTS - From research, NO HARDCODING
// ============================================================================

/**
 * Blood consumption by triage category
 * Source: NATO AMedP-9.1, Ukraine 2022-2024 MTP data
 * 
 * BUG-032 FIX (January 20, 2026):
 * - Problem: BLOOD_CONSUMPTION_RATES.BY_TRIAGE may exist but have different structure
 * - The || fallback only triggers when left side is null/undefined, NOT wrong structure
 * - Solution: Validate nested structure before using imported constants
 */

// Default structure with correct nested properties (used as fallback)
const DEFAULT_BLOOD_BY_TRIAGE = {
  T1_IMMEDIATE: {
    mtp_required_percent: 0.60,      // 60% of T1 need MTP
    mtp_protocol: {
      prbc: 8,
      ffp: 8,
      plt: 6,
      cryo: 10,                       // 10 units cryo per MTP
      txa: 2                          // 2g TXA (1g + 1g)
    },
    non_mtp: {
      prbc: 4,
      ffp: 4,
      plt: 0,
      cryo: 0,
      txa: 1
    }
  },
  T2_DELAYED: {
    transfusion_required_percent: 0.30,  // 30% need transfusion
    units: {
      prbc: 4,
      ffp: 2,
      plt: 0,
      cryo: 0,
      txa: 0.5                        // 50% get TXA
    }
  },
  T3_MINIMAL: {
    transfusion_required_percent: 0.05,  // 5% need transfusion
    units: {
      prbc: 1,
      ffp: 0,
      plt: 0,
      cryo: 0,
      txa: 0
    }
  },
  T4_EXPECTANT: {
    transfusion_required_percent: 0.02,  // Comfort care only
    units: {
      prbc: 0,
      ffp: 0,
      plt: 0,
      cryo: 0,
      txa: 0
    }
  }
};

// BUG-032 FIX: Validate structure before using imported constants
const validateBloodByTriageStructure = (data) => {
  // Check T1_IMMEDIATE has required nested structure
  if (data?.T1_IMMEDIATE?.mtp_protocol?.prbc === undefined) return false;
  if (data?.T1_IMMEDIATE?.non_mtp?.prbc === undefined) return false;
  // Check T2_DELAYED has required structure
  if (data?.T2_DELAYED?.units?.prbc === undefined) return false;
  return true;
};

const importedBloodByTriage = BLOOD_CONSUMPTION_RATES?.BY_TRIAGE;
const BLOOD_BY_TRIAGE = validateBloodByTriageStructure(importedBloodByTriage) 
  ? importedBloodByTriage 
  : DEFAULT_BLOOD_BY_TRIAGE;

/**
 * Surgical requirements by triage
 * Source: NATO surgical planning factors
 * 
 * BUG-032 FIX: Same pattern - validate structure before using imported constants
 */
const DEFAULT_SURGERY_BY_TRIAGE = {
  T1_IMMEDIATE: {
    surgery_required_percent: 0.95,
    avg_duration_hours: 2.5,
    dcs_likely_percent: 0.60,         // 60% need DCS approach
    teams_required: 2                  // Often needs 2 teams
  },
  T2_DELAYED: {
    surgery_required_percent: 0.70,
    avg_duration_hours: 2.0,
    dcs_likely_percent: 0.30,
    teams_required: 1
  },
  T3_MINIMAL: {
    surgery_required_percent: 0.15,
    avg_duration_hours: 1.0,
    dcs_likely_percent: 0.05,
    teams_required: 1
  },
  T4_EXPECTANT: {
    surgery_required_percent: 0.05,
    avg_duration_hours: 0.5,
    dcs_likely_percent: 0,
    teams_required: 1
  }
};

const validateSurgeryByTriageStructure = (data) => {
  if (data?.T1_IMMEDIATE?.surgery_required_percent === undefined) return false;
  if (data?.T1_IMMEDIATE?.avg_duration_hours === undefined) return false;
  return true;
};

const importedSurgeryByTriage = SURGERY_REQUIREMENTS_BY_TRIAGE;
const SURGERY_BY_TRIAGE = validateSurgeryByTriageStructure(importedSurgeryByTriage)
  ? importedSurgeryByTriage
  : DEFAULT_SURGERY_BY_TRIAGE;

/**
 * DCS (Damage Control Surgery) thresholds
 * Source: ATLS, Ukraine surgical data
 * 
 * BUG-035 FIX: Same pattern - validate structure before using imported constants
 */
const DEFAULT_DCS_THRESHOLDS = {
  CASUALTY_THRESHOLD: 50,              // >50 casualties triggers higher DCS ratio
  NORMAL_DCS_RATIO: 0.40,              // 40% DCS in normal operations
  MASCAL_DCS_RATIO: 0.60,              // 60% DCS in MASCAL
  SEVERE_MASCAL_DCS_RATIO: 0.75,       // 75% DCS in severe MASCAL
  
  PHASES: {
    PHASE_1: { name: 'Initial Surgery', duration_hours: 1.0 },
    PHASE_2: { name: 'ICU Resuscitation', duration_hours: 24 },
    PHASE_3: { name: 'Definitive Surgery', duration_hours: 4.0 }
  }
};

const validateDCSStructure = (data) => {
  if (data?.PHASES?.PHASE_1?.duration_hours === undefined) return false;
  if (data?.NORMAL_DCS_RATIO === undefined) return false;
  return true;
};

const importedDCSThresholds = DCS_PHASES;
const DCS_THRESHOLDS = validateDCSStructure(importedDCSThresholds)
  ? importedDCSThresholds
  : DEFAULT_DCS_THRESHOLDS;

/**
 * Surgical team fatigue model
 * Source: OR efficiency studies, Ukraine surgical team data
 * 
 * Note: This one has proper fallback in usage (line 576), but adding validation for consistency
 */
const DEFAULT_SURGICAL_FATIGUE = {
  HOURS_0_12: { efficiency: 1.00, error_rate: 0.02 },
  HOURS_12_18: { efficiency: 0.85, error_rate: 0.05 },
  HOURS_18_24: { efficiency: 0.70, error_rate: 0.10 },
  HOURS_24_PLUS: { efficiency: 0.50, error_rate: 0.20 },
  
  MANDATORY_REST_HOURS: 8,
  RECOMMENDED_SHIFT_HOURS: 12
};

const validateSurgicalFatigueStructure = (data) => {
  if (data?.HOURS_0_12?.efficiency === undefined) return false;
  return true;
};

const importedSurgicalFatigue = SURGICAL_FATIGUE_MODEL;
const SURGICAL_FATIGUE = validateSurgicalFatigueStructure(importedSurgicalFatigue)
  ? importedSurgicalFatigue
  : DEFAULT_SURGICAL_FATIGUE;

// ============================================================================
// BLOOD PRODUCT FUNCTIONS
// ============================================================================

/**
 * Calculate blood product requirements
 * 
 * @param {Object} config - Configuration
 * @returns {Object} Blood requirements analysis
 */
// 2026-10-18 (Tab 3 review, surgical rates check): the calculator hands over the triage counts of the wounded as T1 / T2 / T3 / T4
// (triageCategories.calculateTriageDistribution); this module looked for t1_immediate / t2_delayed / t3_minimal, found nothing and
// always fell back to 25 / 40 / 30 percent of ALL casualties of the day, killed in action included. So the blood and surgical
// demand never followed the triage of the wounded. true = read the counts the calculator hands over; false = the former fallback.
export const TRIAGE_COUNTS_FROM_WIA = true;

function triageCount(distribution, names, share, dailyCasualties) {
  if (TRIAGE_COUNTS_FROM_WIA && distribution) {
    for (const name of names) {
      if (typeof distribution[name] === 'number' && Number.isFinite(distribution[name])) return distribution[name];
    }
  }
  return (distribution && distribution[names[0]]) || Math.round(dailyCasualties * share);
}

export function calculateBloodRequirementsDetailed(config) {
  const {
    triageDistribution,
    dailyCasualties = 50,
    exactCounts = false   // 2026-10-21: true = the counts of patients needing blood are not rounded per triage category (fraction carry)
  } = config;
  const rnd = exactCounts ? (v) => v : Math.round;

  // Get triage counts
  const t1Count = triageCount(triageDistribution, ['t1_immediate', 'T1', 't1'], 0.25, dailyCasualties);
  const t2Count = triageCount(triageDistribution, ['t2_delayed', 'T2', 't2'], 0.40, dailyCasualties);
  const t3Count = triageCount(triageDistribution, ['t3_minimal', 'T3', 't3'], 0.30, dailyCasualties);
  const t4Count = triageDistribution?.t4_expectant || Math.round(dailyCasualties * 0.05);

  // T1 - Immediate
  const t1Config = BLOOD_BY_TRIAGE.T1_IMMEDIATE;
  const t1NeedingMTP = rnd(t1Count * t1Config.mtp_required_percent);
  const t1NonMTP = t1Count - t1NeedingMTP;

  const t1Blood = {
    mtp_cases: t1NeedingMTP,
    non_mtp_cases: t1NonMTP,
    prbc: (t1NeedingMTP * t1Config.mtp_protocol.prbc) + (t1NonMTP * t1Config.non_mtp.prbc),
    ffp: (t1NeedingMTP * t1Config.mtp_protocol.ffp) + (t1NonMTP * t1Config.non_mtp.ffp),
    plt: t1NeedingMTP * t1Config.mtp_protocol.plt,
    cryo: t1NeedingMTP * t1Config.mtp_protocol.cryo,
    txa: (t1NeedingMTP * t1Config.mtp_protocol.txa) + (t1NonMTP * t1Config.non_mtp.txa)
  };

  // T2 - Delayed
  const t2Config = BLOOD_BY_TRIAGE.T2_DELAYED;
  const t2NeedingTransfusion = rnd(t2Count * t2Config.transfusion_required_percent);

  const t2Blood = {
    transfusion_cases: t2NeedingTransfusion,
    prbc: t2NeedingTransfusion * t2Config.units.prbc,
    ffp: t2NeedingTransfusion * t2Config.units.ffp,
    plt: t2NeedingTransfusion * t2Config.units.plt,
    cryo: t2NeedingTransfusion * t2Config.units.cryo,
    txa: rnd(t2Count * t2Config.units.txa)
  };

  // T3 - Minimal
  const t3Config = BLOOD_BY_TRIAGE.T3_MINIMAL;
  const t3NeedingTransfusion = rnd(t3Count * t3Config.transfusion_required_percent);

  const t3Blood = {
    transfusion_cases: t3NeedingTransfusion,
    prbc: t3NeedingTransfusion * t3Config.units.prbc,
    ffp: 0,
    plt: 0,
    cryo: 0,
    txa: 0
  };

  // T4 - Expectant (comfort care - minimal resources)
  const t4Config = BLOOD_BY_TRIAGE.T4_EXPECTANT;
  const t4NeedingTransfusion = rnd(t4Count * t4Config.transfusion_required_percent);

  const t4Blood = {
    transfusion_cases: t4NeedingTransfusion,
    prbc: t4NeedingTransfusion * t4Config.units.prbc,
    ffp: 0,
    plt: 0,
    cryo: 0,
    txa: 0,
    comfort_care_only: true
  };

  // Totals (including T4 for complete accounting)
  const totals = {
    prbc: t1Blood.prbc + t2Blood.prbc + t3Blood.prbc + t4Blood.prbc,
    ffp: t1Blood.ffp + t2Blood.ffp + t3Blood.ffp + t4Blood.ffp,
    plt: t1Blood.plt + t2Blood.plt + t3Blood.plt + t4Blood.plt,
    cryo: t1Blood.cryo + t2Blood.cryo + t3Blood.cryo + t4Blood.cryo,
    txa_grams: t1Blood.txa + t2Blood.txa + t3Blood.txa + t4Blood.txa,
    whole_blood_equivalent: 0  // Calculated below
  };

  // Whole blood equivalent (for WBB planning)
  totals.whole_blood_equivalent = rnd(totals.prbc * 0.8);

  return {
    by_category: {
      t1_immediate: t1Blood,
      t2_delayed: t2Blood,
      t3_minimal: t3Blood,
      t4_expectant: t4Blood
    },
    
    totals,
    
    summary: {
      total_units: totals.prbc + totals.ffp + totals.plt + totals.cryo,
      mtp_cases: t1NeedingMTP,
      transfusion_cases: t1Count + t2NeedingTransfusion + t3NeedingTransfusion + t4NeedingTransfusion,
      txa_doses: Math.ceil(totals.txa_grams)
    },
    
    // MTP evaluation using imported constants
    mtp_analysis: {
      cases_requiring_mtp: t1NeedingMTP,
      protocol: MASSIVE_TRANSFUSION_PROTOCOL?.name || 'Massive Transfusion Protocol',
      ratio: MASSIVE_TRANSFUSION_PROTOCOL?.standard_ratios?.balanced || '1:1:1',
      activation_criteria: MASSIVE_TRANSFUSION_PROTOCOL?.triggers?.clinical || [
        'Anticipated need for >10 PRBC in 24h',
        'Ongoing hemorrhage with hemodynamic instability'
      ]
    },
    
    // Educational context from imported constants
    educational_context: {
      triage_definitions: {
        t1: TRIAGE_CATEGORIES?.T1_IMMEDIATE?.description || 'Life-threatening, immediate surgery',
        t2: TRIAGE_CATEGORIES?.T2_DELAYED?.description || 'Serious but can wait 4-6 hours',
        t3: TRIAGE_CATEGORIES?.T3_MINIMAL?.description || 'Minor injuries, ambulatory',
        t4: TRIAGE_CATEGORIES?.T4_EXPECTANT?.description || 'Expectant/comfort care'
      },
      blood_products: {
        prbc: BLOOD_PRODUCTS?.PRBC?.name || 'Packed Red Blood Cells',
        ffp: BLOOD_PRODUCTS?.FFP?.name || 'Fresh Frozen Plasma',
        plt: BLOOD_PRODUCTS?.PLT?.name || 'Platelets',
        cryo: BLOOD_PRODUCTS?.CRYO?.name || 'Cryoprecipitate'
      }
    },
    
    // Cross-check with constants-based calculation
    constants_comparison: calculateBloodRequirements({
      t1: t1Count,
      t2: t2Count,
      t3: t3Count
    }),
    
    educational_note: `${t1NeedingMTP} T1 casualties require MTP (8:8:6 PRBC:FFP:PLT ratio). ` +
      `Total blood requirement: ${totals.prbc} PRBC, ${totals.ffp} FFP, ${totals.plt} PLT, ${totals.cryo} Cryo.`
  };
}

/**
 * Calculate Walking Blood Bank requirements
 * 
 * @param {Object} config - Configuration
 * @returns {Object} WBB analysis
 */
export function calculateWBBRequirements(config) {
  const {
    bloodShortfall = 0,
    personnelStrength = 4500,
    bloodAlertLevel = 'GREEN'
  } = config;

  // WBB configuration from constants
  // BUG-036 FIX: Validate structure before using
  const DEFAULT_WBB_CONFIG = {
    eligible_percent: 0.025,           // 2.5% can donate safely
    units_per_donor: 1,
    collection_time_minutes: 45,
    recovery_days: 56,
    max_donors_per_day_percent: 0.01   // Max 1% of unit per day
  };
  const wbbConfig = (WALKING_BLOOD_BANK?.eligible_percent !== undefined)
    ? WALKING_BLOOD_BANK
    : DEFAULT_WBB_CONFIG;

  // Use imported function for precise donor pool calculation
  const wbbPoolData = calculateWBBDonorRequirement(personnelStrength);
  
  // Enhanced donor pool data from constants
  const eligibleDonors = wbbPoolData?.expected_available || 
                         Math.round(personnelStrength * wbbConfig.eligible_percent);
  const ltowbDonors = wbbPoolData?.ltowb_donors || Math.round(eligibleDonors * 0.35);
  const targetRegisteredDonors = wbbPoolData?.target_registered_donors || eligibleDonors;
  const maxDonorsPerDay = Math.round(personnelStrength * (wbbConfig.max_donors_per_day_percent || 0.01));

  // Calculate if WBB needed
  const wbbNeeded = bloodShortfall > 0 || bloodAlertLevel === 'RED' || bloodAlertLevel === 'BLACK';

  if (!wbbNeeded) {
    return {
      needed: false,
      reason: 'Blood supply adequate',
      eligible_donors: eligibleDonors,
      ltowb_donors: ltowbDonors,
      target_registered: targetRegisteredDonors,
      educational_note: 'Walking Blood Bank on standby. Can provide fresh whole blood if supply runs low. ' +
        `${ltowbDonors} O+ low-titer donors available for universal whole blood.`
    };
  }

  // Calculate donors needed
  const donorsNeeded = Math.ceil(bloodShortfall * 1.2);  // 20% safety margin
  const daysToCollect = Math.ceil(donorsNeeded / maxDonorsPerDay);
  const canMeetDemand = eligibleDonors >= donorsNeeded;

  // Calculate collection time
  const collectionTeams = 2;  // Typical for brigade
  const collectionsPerTeamPerHour = 60 / (wbbConfig.collection_time_minutes || 45);
  const hoursToCollect = donorsNeeded / (collectionTeams * collectionsPerTeamPerHour);

  // DOW impact of WBB
  const wbbDOWReduction = canMeetDemand ? 0.15 : (eligibleDonors / donorsNeeded) * 0.15;

  return {
    needed: true,
    
    demand: {
      blood_shortfall_units: bloodShortfall,
      donors_needed: donorsNeeded,
      can_meet_demand: canMeetDemand,
      coverage_percent: Math.min(100, Math.round((eligibleDonors / donorsNeeded) * 100))
    },
    
    capacity: {
      eligible_donors: eligibleDonors,
      max_donors_per_day: maxDonorsPerDay,
      days_to_collect: daysToCollect,
      hours_to_collect: Math.round(hoursToCollect * 10) / 10,
      collection_teams: collectionTeams
    },
    
    impact: {
      dow_reduction_percent: Math.round(wbbDOWReduction * 100),
      units_provided: Math.min(eligibleDonors, donorsNeeded)
    },
    
    recommendations: generateWBBRecommendations(canMeetDemand, daysToCollect, bloodAlertLevel),
    
    educational_note: `Fresh whole blood from WBB is superior to stored components. ` +
      `${eligibleDonors} eligible donors available (2.5% of ${personnelStrength}). ` +
      `Collection time: ~${Math.round(hoursToCollect)}h for ${donorsNeeded} units.`
  };
}

/**
 * Generate WBB recommendations
 */
function generateWBBRecommendations(canMeet, days, alertLevel) {
  const recs = [];

  if (alertLevel === 'BLACK') {
    recs.push('CRITICAL: Activate WBB immediately');
    recs.push('Request emergency blood resupply via Blue Light Protocol');
  } else if (alertLevel === 'RED') {
    recs.push('URGENT: Begin WBB donor screening now');
    recs.push('Pre-position collection teams');
  }

  if (!canMeet) {
    recs.push('WARNING: Donor pool insufficient - request external blood support');
    recs.push('Consider triage to conserve blood for survivable casualties');
  }

  if (days > 1) {
    recs.push(`Collection requires ${days} days - start immediately`);
  }

  return recs;
}

// ============================================================================
// SURGICAL CAPACITY FUNCTIONS
// ============================================================================

/**
 * Calculate surgical requirements and capacity
 * 
 * @param {Object} config - Configuration
 * @returns {Object} Surgical analysis
 */
export function calculateSurgicalRequirements(config) {
  const {
    triageDistribution,
    dailyCasualties = 50,
    surgicalTeams = 2,
    hoursIntoShift = 0,
    surgicalBacklog = 0,
    mascalLevel = 'NORMAL',
    tempo = null,
    exactCounts = false   // 2026-10-21: true = cases, hours and capacity are not rounded to whole numbers (fraction carry)
  } = config;
  const rnd = exactCounts ? (v) => v : Math.round;

  // Get triage counts
  const t1Count = triageCount(triageDistribution, ['t1_immediate', 'T1', 't1'], 0.25, dailyCasualties);
  const t2Count = triageCount(triageDistribution, ['t2_delayed', 'T2', 't2'], 0.40, dailyCasualties);
  const t3Count = triageCount(triageDistribution, ['t3_minimal', 'T3', 't3'], 0.30, dailyCasualties);

  // Calculate surgical demand by category
  const t1Surgical = {
    cases: rnd(t1Count * SURGERY_BY_TRIAGE.T1_IMMEDIATE.surgery_required_percent),
    hours: rnd(t1Count * SURGERY_BY_TRIAGE.T1_IMMEDIATE.surgery_required_percent * 
           SURGERY_BY_TRIAGE.T1_IMMEDIATE.avg_duration_hours),
    dcs_cases: 0  // Calculated below
  };

  const t2Surgical = {
    cases: rnd(t2Count * SURGERY_BY_TRIAGE.T2_DELAYED.surgery_required_percent),
    hours: rnd(t2Count * SURGERY_BY_TRIAGE.T2_DELAYED.surgery_required_percent * 
           SURGERY_BY_TRIAGE.T2_DELAYED.avg_duration_hours)
  };

  const t3Surgical = {
    cases: rnd(t3Count * SURGERY_BY_TRIAGE.T3_MINIMAL.surgery_required_percent),
    hours: rnd(t3Count * SURGERY_BY_TRIAGE.T3_MINIMAL.surgery_required_percent * 
           SURGERY_BY_TRIAGE.T3_MINIMAL.avg_duration_hours)
  };

  // Total demand
  const totalCases = t1Surgical.cases + t2Surgical.cases + t3Surgical.cases;
  const totalHours = t1Surgical.hours + t2Surgical.hours + t3Surgical.hours;

  // DCS ratio - based on casualty load and MASCAL level
  let dcsRatio = DCS_THRESHOLDS.NORMAL_DCS_RATIO;
  if (dailyCasualties > DCS_THRESHOLDS.CASUALTY_THRESHOLD || mascalLevel !== 'NORMAL') {
    dcsRatio = DCS_THRESHOLDS.MASCAL_DCS_RATIO;
  }
  if (mascalLevel === 'MASCAL_2' || mascalLevel === 'MASCAL_3') {
    dcsRatio = DCS_THRESHOLDS.SEVERE_MASCAL_DCS_RATIO;
  }

  // Calculate DCS vs definitive
  const dcsCases = rnd(t1Surgical.cases * dcsRatio);
  const definitiveCases = t1Surgical.cases - dcsCases;
  t1Surgical.dcs_cases = dcsCases;

  // DCS saves time (1h vs 2.5h)
  const dcsHoursSaved = dcsCases * (SURGERY_BY_TRIAGE.T1_IMMEDIATE.avg_duration_hours - 
                                     DCS_THRESHOLDS.PHASES.PHASE_1.duration_hours);
  const adjustedTotalHours = totalHours - dcsHoursSaved;

  // Calculate capacity using imported function
  const surgicalCapData = calculateSurgicalCapacity({
    surgical_teams: surgicalTeams,
    operating_tables: surgicalTeams,  // Assume 1:1 ratio if not specified
    hours_per_day: 16,
    avg_procedure_minutes: 90
  });
  
  // Use imported function for precise fatigue curve
  const performanceData = getSurgeonPerformance(hoursIntoShift);
  
  // Legacy fatigue level for backwards compatibility
  const fatigueLevel = hoursIntoShift <= 12 ? 'HOURS_0_12' :
                       hoursIntoShift <= 18 ? 'HOURS_12_18' :
                       hoursIntoShift <= 24 ? 'HOURS_18_24' : 'HOURS_24_PLUS';
  const fatigueEffect = SURGICAL_FATIGUE[fatigueLevel] || { efficiency: performanceData.performance, error_rate: performanceData.error_rate };

  // Use imported surgical capacity data
  const teamCapacity = SURGICAL_CAPACITY_BY_MTF?.ROLE_2?.teams || { cases_per_day_normal: 6, cases_per_day_surge: 10 };
  const baseCapacity = surgicalCapData?.procedures_per_day || (surgicalTeams * teamCapacity.cases_per_day_normal);
  const surgeCapacity = surgicalTeams * teamCapacity.cases_per_day_surge;
  const limitingFactor = surgicalCapData?.limiting_factor || 'UNKNOWN';
  
  // Apply fatigue using imported performance curve
  const effectiveCapacity = tempo
    ? rnd(baseCapacity * tempo.capacity_ratio * tempo.performance)
    : rnd(baseCapacity * performanceData.performance);

  // Calculate queue
  const totalDemand = totalCases + surgicalBacklog;
  const casesCompleted = Math.min(totalDemand, effectiveCapacity);
  const newBacklog = totalDemand - casesCompleted;
  const avgWaitHours = newBacklog > 0 ? Math.round((newBacklog / (effectiveCapacity / 24)) * 10) / 10 : 0;

  // Parallel teams needed for T1
  const parallelTeamsNeeded = Math.ceil(t1Surgical.cases / 2);  // Can't delay T1

  return {
    demand: {
      by_category: {
        t1_immediate: t1Surgical,
        t2_delayed: t2Surgical,
        t3_minimal: t3Surgical
      },
      total_cases: totalCases,
      total_hours: totalHours,
      adjusted_hours: Math.round(adjustedTotalHours),
      with_backlog: totalDemand
    },
    
    dcs_analysis: {
      ratio_applied: Math.round(dcsRatio * 100),
      dcs_cases: dcsCases,
      definitive_cases: definitiveCases,
      hours_saved: Math.round(dcsHoursSaved),
      phase_1_cases: dcsCases,
      phase_3_pending: dcsCases  // Will need definitive surgery later
    },
    
    capacity: {
      teams: surgicalTeams,
      base_cases_per_day: baseCapacity,
      surge_cases_per_day: surgeCapacity,
      effective_capacity: effectiveCapacity,
      limiting_factor: limitingFactor,
      fatigue_level: fatigueLevel,
      efficiency_percent: Math.round(performanceData.performance * 100),
      error_rate_percent: Math.round(performanceData.error_rate * 100),
      decision_quality_percent: Math.round((performanceData.decision_quality || 0.9) * 100),
      hours_worked: performanceData.hours_worked
    },
    
    queue: {
      cases_completed: casesCompleted,
      new_backlog: newBacklog,
      average_wait_hours: avgWaitHours,
      parallel_teams_needed: parallelTeamsNeeded,
      is_over_capacity: totalDemand > effectiveCapacity
    },
    
    utilization_percent: Math.round((totalDemand / effectiveCapacity) * 100),
    
    recommendations: generateSurgicalRecommendations(
      totalDemand, effectiveCapacity, fatigueLevel, newBacklog, parallelTeamsNeeded, surgicalTeams
    ),
    
    educational_note: `${totalCases} surgical cases requiring ${Math.round(adjustedTotalHours)}h OR time. ` +
      `DCS ratio ${Math.round(dcsRatio * 100)}% saves ${Math.round(dcsHoursSaved)}h. ` +
      `${surgicalTeams} teams at ${Math.round(fatigueEffect.efficiency * 100)}% efficiency (${fatigueLevel}).`
  };
}

/**
 * Generate surgical recommendations
 */
function generateSurgicalRecommendations(demand, capacity, fatigue, backlog, parallel, teams) {
  const recs = [];

  if (demand > capacity * 1.5) {
    recs.push('CRITICAL: Surgical capacity severely exceeded - implement crisis standards');
    recs.push('Increase DCS ratio to conserve OR time');
  } else if (demand > capacity) {
    recs.push('WARNING: Surgical capacity exceeded - backlog forming');
  }

  if (fatigue === 'HOURS_18_24' || fatigue === 'HOURS_24_PLUS') {
    recs.push('URGENT: Rotate surgical teams - fatigue increasing error rate');
  }

  if (parallel > teams) {
    recs.push(`Need ${parallel} parallel teams for T1 - only have ${teams}`);
    recs.push('Consider requesting additional surgical support');
  }

  if (backlog > capacity * 0.5) {
    recs.push(`Surgical backlog of ${backlog} cases - T2 wait times increasing`);
  }

  return recs;
}

/**
 * Calculate surgical queue cascade effects
 * Models how backlog from previous day affects current day
 * 
 * @param {Object} config - Configuration
 * @returns {Object} Queue cascade analysis
 */
export function calculateSurgicalQueueCascade(config) {
  const {
    previousDayBacklog = 0,
    previousDayCompletionRate = 1.0,
    newCases = 30,
    surgicalTeams = 2,
    dayOfOperation = 1,
    tempo = null
  } = config;

  // Cumulative fatigue reduces capacity over days. With the tempo model the fatigue is the tempo's own (performance of the
  // shift length, sleep debt) and the capacity per team follows the tempo; without it the former 3 percent a day applies.
  const dayFatigueFactor = tempo ? tempo.performance : Math.max(0.7, 1 - (dayOfOperation - 1) * 0.03);

  // Previous day's performance affects team morale/efficiency
  // High completion rate = morale boost, low rate = demoralization
  const completionMoraleBonus = previousDayCompletionRate >= 0.9 ? 1.05 :   // Good performance = +5%
                                previousDayCompletionRate >= 0.7 ? 1.00 :   // Acceptable = no change
                                previousDayCompletionRate >= 0.5 ? 0.95 :   // Poor = -5%
                                                                   0.90;    // Very poor = -10%

  // Calculate today's capacity with both fatigue and morale factors
  const baseCapacity = tempo ? surgicalTeams * tempo.cases_per_team : surgicalTeams * 6;
  const effectiveCapacity = Math.round(baseCapacity * dayFatigueFactor * completionMoraleBonus);

  // Total cases to handle
  const totalCases = previousDayBacklog + newCases;

  // Process cases
  const completed = Math.min(totalCases, effectiveCapacity);
  const endOfDayBacklog = totalCases - completed;

  // Calculate T2 mortality from delay
  // Each hour of delay adds ~3% mortality to T2
  const avgBacklogWaitHours = endOfDayBacklog > 0 ? 
    (endOfDayBacklog / (effectiveCapacity / 24)) : 0;
  const t2DelayMortality = Math.min(0.15, avgBacklogWaitHours * 0.03);

  // Calculate additional DOW from queue
  const estimatedT2InBacklog = Math.round(endOfDayBacklog * 0.5);  // 50% of backlog is T2
  const additionalDOW = Math.round(estimatedT2InBacklog * t2DelayMortality);

  return {
    input: {
      previous_backlog: previousDayBacklog,
      previous_completion_rate: previousDayCompletionRate,
      new_cases: newCases,
      total_cases: totalCases
    },
    
    capacity: {
      base: baseCapacity,
      effective: effectiveCapacity,
      day_fatigue_factor: Math.round(dayFatigueFactor * 100),
      morale_factor: Math.round(completionMoraleBonus * 100),
      day_of_operation: dayOfOperation
    },
    
    output: {
      completed: completed,
      end_of_day_backlog: endOfDayBacklog,
      avg_wait_hours: Math.round(avgBacklogWaitHours * 10) / 10
    },
    
    mortality_impact: {
      delay_mortality_rate: Math.round(t2DelayMortality * 1000) / 10,
      estimated_t2_in_backlog: estimatedT2InBacklog,
      additional_dow: additionalDOW
    },
    
    cascade_warning: endOfDayBacklog > previousDayBacklog ? 
      'ESCALATING: Backlog growing - system approaching collapse' : null
  };
}

// ============================================================================
// CLASS VIII (MEDICAL SUPPLIES) FUNCTIONS
// ============================================================================

/**
 * Calculate medical supply consumption
 * 
 * @param {Object} config - Configuration
 * @returns {Object} Supply consumption analysis
 */
export function calculateMedicalSupplyConsumption(config) {
  const {
    dailyCasualties = 50,
    currentStock = {},
    dailyResupply = {},
    triageDistribution = null
  } = config;

  // MDOS standards from constants for threshold evaluation
  // BUG-036 FIX: Validate structure before using
  const DEFAULT_MDOS_STANDARDS = {
    LINE_1: { days: 3 },
    LINE_2: { days: 4 },
    LINE_3: { days: 13 },
    TOTAL: { days: 20 }
  };
  const mdosStandards = (MDOS_STANDARDS?.LINE_1?.days !== undefined) 
    ? MDOS_STANDARDS 
    : DEFAULT_MDOS_STANDARDS;

  // BUG-036 FIX: Validate CLASS_VIII_CATEGORIES structure
  const DEFAULT_CONSUMPTION_FACTORS = {
    MEDICAL_MATERIEL: { kg_per_casualty: 5 },
    PHARMACEUTICALS: { kg_per_casualty: 2 },
    SURGICAL_SUPPLIES: { kg_per_casualty: 3 },
    IV_FLUIDS: { liters_per_casualty: 6 }
  };
  const consumptionFactors = (CLASS_VIII_CATEGORIES?.MEDICAL_MATERIEL?.kg_per_casualty !== undefined) 
    ? CLASS_VIII_CATEGORIES 
    : DEFAULT_CONSUMPTION_FACTORS;

  // Use per-triage consumption rates from constants if available
  const perTriageRates = CONSUMPTION_RATES || {};
  
  // Calculate detailed consumption if triage distribution provided
  let detailedConsumption = null;
  if (triageDistribution && perTriageRates.T1_IMMEDIATE) {
    // RM-02 FIX: Replace hardcoded 0.15/0.25/0.30 triage distribution fallbacks
    // with DEFAULT_CASUALTY_MIX imported from classVIII.js.
    // These are the fallback proportions used when triageDistribution is not provided.
    // DEFAULT_CASUALTY_MIX is the single authoritative source for baseline triage split.
    const t1Count = triageDistribution.t1_immediate || Math.round(dailyCasualties * DEFAULT_CASUALTY_MIX.t1);
    const t2Count = triageDistribution.t2_delayed   || Math.round(dailyCasualties * DEFAULT_CASUALTY_MIX.t2);
    const t3Count = triageDistribution.t3_minimal   || Math.round(dailyCasualties * DEFAULT_CASUALTY_MIX.t3);
    
    const t1Rates = perTriageRates.T1_IMMEDIATE?.pharmaceuticals || {};
    const t2Rates = perTriageRates.T2_DELAYED?.pharmaceuticals || {};
    const t3Rates = perTriageRates.T3_MINIMAL?.pharmaceuticals || {};
    
    detailedConsumption = {
      iv_fluids_liters: (t1Count * (t1Rates.iv_fluids_liters || 6)) + 
                        (t2Count * (t2Rates.iv_fluids_liters || 3)) +
                        (t3Count * (t3Rates.iv_fluids_liters || 1)),
      txa_grams: (t1Count * (t1Rates.txa_grams || 2)) + 
                 (t2Count * (t2Rates.txa_grams || 1)),
      ketamine_mg: (t1Count * (t1Rates.ketamine_mg || 500)) + 
                   (t2Count * (t2Rates.ketamine_mg || 200)),
      morphine_doses: (t1Count * (t1Rates.morphine_doses || 2)) + 
                      (t2Count * (t2Rates.morphine_doses || 1)) +
                      (t3Count * (t3Rates.morphine_doses || 0.5)),
      antibiotics_doses: (t1Count * (t1Rates.antibiotics_doses || 3)) + 
                         (t2Count * (t2Rates.antibiotics_doses || 2)) +
                         (t3Count * (t3Rates.antibiotics_doses || 1))
    };
  }

  // Calculate daily consumption
  const dailyConsumption = {
    medical_materiel_kg: dailyCasualties * consumptionFactors.MEDICAL_MATERIEL.kg_per_casualty,
    pharmaceuticals_kg: dailyCasualties * consumptionFactors.PHARMACEUTICALS.kg_per_casualty,
    surgical_supplies_kg: dailyCasualties * consumptionFactors.SURGICAL_SUPPLIES.kg_per_casualty,
    iv_fluids_liters: detailedConsumption?.iv_fluids_liters || 
                      (dailyCasualties * consumptionFactors.IV_FLUIDS.liters_per_casualty),
    // RM-01 FIX: Replace `(5 + 2 + 3)` inline sum with explicit field references.
    // Source: consumptionFactors is already validated above from CLASS_VIII_CATEGORIES.
    // Old code obscured which categories were being summed and masked if any value changed.
    total_weight_kg: dailyCasualties * (
      consumptionFactors.MEDICAL_MATERIEL.kg_per_casualty +
      consumptionFactors.PHARMACEUTICALS.kg_per_casualty +
      consumptionFactors.SURGICAL_SUPPLIES.kg_per_casualty
    )
  };

  // Calculate MDOS (Medical Days of Supply)
  const mdosCalc = {};
  for (const [category, consumption] of Object.entries(dailyConsumption)) {
    if (category === 'total_weight_kg') continue;
    const stock = currentStock[category] || 0;
    const resupply = dailyResupply[category] || 0;
    const dos = consumption > 0 ? Math.round((stock / consumption) * 10) / 10 : 999;
    
    mdosCalc[category] = {
      current_stock: stock,
      daily_consumption: consumption,
      daily_resupply: resupply,
      net_daily_change: resupply - consumption,
      days_of_supply: dos,
      // Use mdosStandards for status evaluation
      status: dos >= mdosStandards.LINE_3.days ? 'GREEN' :
              dos >= mdosStandards.LINE_2.days ? 'YELLOW' :
              dos >= mdosStandards.LINE_1.days ? 'AMBER' : 'RED'
    };
  }

  // Use imported calculateMDOS if available for comprehensive analysis
  let mdosFromConstants = null;
  if (typeof calculateMDOS === 'function' && Object.keys(currentStock).length > 0) {
    try {
      mdosFromConstants = calculateMDOS(currentStock, dailyCasualties, triageDistribution);
    } catch (e) {
      // Function not available or error - continue with local calculation
    }
  }

  // Critical items check
  const criticalItems = [
    'blood_products', 'anesthetics', 'antibiotics', 'morphine',
    'surgical_instruments', 'ventilators', 'oxygen', 'iv_fluids'
  ];

  return {
    daily_consumption: dailyConsumption,
    detailed_pharmaceutical_consumption: detailedConsumption,
    mdos_analysis: mdosCalc,
    mdos_from_constants: mdosFromConstants,
    critical_items: criticalItems,
    
    // DOS thresholds from constants for reference
    dos_thresholds: {
      line_1_minimum: mdosStandards.LINE_1.days,
      line_2_minimum: mdosStandards.LINE_2.days,
      line_3_target: mdosStandards.LINE_3.days,
      total_objective: mdosStandards.TOTAL.days
    },
    
    supply_status: {
      adequate: Object.values(mdosCalc).every(c => c.days_of_supply >= mdosStandards.LINE_1.days),
      warning: Object.values(mdosCalc).some(c => c.days_of_supply < mdosStandards.LINE_1.days && c.days_of_supply >= 1),
      critical: Object.values(mdosCalc).some(c => c.days_of_supply < 1)
    },
    
    resupply_requirement: {
      weight_per_day_kg: dailyConsumption.total_weight_kg,
      convoy_loads: Math.ceil(dailyConsumption.total_weight_kg / 5000)  // 5 ton truck
    }
  };
}

// ============================================================================
// COMPREHENSIVE RESOURCE ANALYSIS
// ============================================================================

// 2026-10-18 (Tab 3 review, Dr. Tatar): the surgical capacity of the day follows the operations TEMPO (normal / surge / crisis),
// derived each day from the load and capped by the planner's policy (surgical_config.max_tempo); see constants/surgicalTempo.js.
// It replaces the made-up shift pattern ((day - 1) mod 2 x 12 + 6 hours) and the day factor of 3 percent a day of the queue
// cascade, and the fatigue division that the calculator applied to the backlog. false = the former three mechanisms.
export const SURGICAL_TEMPO_FROM_LOAD = true;
// Cases a surgical team does in a day at the normal tempo (the figure the queue cascade has always used).
export const NORMAL_CASES_PER_TEAM_PER_DAY = 6;

// 2026-10-18 (Tab 3 review, Dr. Tatar): SURGICAL REGULATING. The cases the Role 2 Basic cannot take without going into crisis tempo
// are routed to a neighbour Role 2 Basic, the Role 2 Enhanced and the Role 3 (accessibility switches of Tab 3), each with its
// capacity and extra transit; what nobody takes stays in the queue. false = every case of the brigade on its Role 2 Basic (former).
// With the regulating on, the Surgical Company of Tab 3 is capacity of the Role 2 Enhanced, not of the Role 2 Basic.
export const SURGICAL_REGULATING_ON = true;

/**
 * Surgical teams of the brigade: the number computed by the calculator from the Tab 3 team counts
 * (medicalConfig.surgical_teams_per_brigade, 2026-10-18 T3-02), else the former reading (teams.length, a planning default
 * with an assumption entry when absent). A planner's zero is a real value (no surgical team).
 */
function resolveSurgicalTeams(medicalConfig) {
  const own = medicalConfig.surgical_teams_per_brigade;
  if (typeof own === 'number' && Number.isFinite(own) && own >= 0) return own;
  return resolveInput(medicalConfig.surgical_config?.teams?.length, 'surgical_teams', { zeroIsMissing: true });
}

/**
 * Calculate comprehensive medical resource requirements
 * 
 * @param {Object} config - Complete configuration
 * @returns {Object} Complete resource analysis
 */
export function calculateComprehensiveResources(config) {
  const {
    triageDistribution,
    dailyCasualties = 50,
    medicalConfig = {},
    day = 1,
    previousDayState = {}
  } = config;

  // Blood requirements
  const bloodAnalysis = calculateBloodRequirementsDetailed({
    triageDistribution,
    dailyCasualties,
    exactCounts: config.exactCounts === true
  });

  // Check blood status
  const currentBloodStock = resolveInput(medicalConfig.blood_config?.stock?.prbc, 'blood_stock_prbc', { zeroIsMissing: true });
  const bloodShortfall = Math.max(0, bloodAnalysis.totals.prbc - currentBloodStock);
  const bloodAlertLevel = bloodShortfall > currentBloodStock * 0.5 ? 'RED' :
                          bloodShortfall > 0 ? 'YELLOW' : 'GREEN';

  // WBB requirements if needed
  const wbbAnalysis = calculateWBBRequirements({
    bloodShortfall,
    personnelStrength: resolveInput(medicalConfig.personnel_strength, 'brigade_strength', { zeroIsMissing: true }),
    bloodAlertLevel
  });

  // Surgical requirements with cascade
  // R2-03 / R2-04: facilities hit by enemy fire (protection level), power and water of the infrastructure card lower the surgical capacity: effective teams
  const _teams = resolveSurgicalTeams(medicalConfig) * (typeof config.facilityCapacityFactor === 'number' ? config.facilityCapacityFactor : 1);
  const _mascalLevel = config.mascalLevel || medicalConfig.mascal_level || 'NORMAL';
  const surgicalDemand = calculateSurgicalRequirements({
    triageDistribution,
    dailyCasualties,
    surgicalTeams: _teams,
    hoursIntoShift: (day - 1) % 2 * 12 + 6,  // former shift pattern (used when the tempo model is off)
    surgicalBacklog: previousDayState.surgical_backlog || 0,
    mascalLevel: _mascalLevel,
    exactCounts: config.exactCounts === true
  });
  // Operations tempo of the day (Role 2 Basic): from the load, capped by the planner's policy.
  // Surgical regulating: what the Role 2 Basic cannot take goes to the other facilities that are accessible.
  let regulating = null;
  // civilian cases the Role 2 Enhanced and Role 3 cannot take fall on the Role 2 Basic (per brigade); without regulating all of them (R2-08)
  let _civToR2B = (config.civilianSurgicalCases || 0) / Math.max(1, config.brigadeCount || 1);
  if (SURGICAL_REGULATING_ON) {
    const built = buildSurgicalDestinations({
      medicalConfig,
      brigadeTeams: _teams,
      brigadeCount: config.brigadeCount || 1,
      normalCasesPerTeam: NORMAL_CASES_PER_TEAM_PER_DAY,
      civilianCases: config.civilianSurgicalCases || 0   // R2-08: civilian cases the military takes (force)
    });
    _civToR2B = (built.enabled ? (built.civilian_to_r2b || 0) : (config.civilianSurgicalCases || 0)) / Math.max(1, config.brigadeCount || 1);
    if (built.enabled) {
      regulating = {
        ...regulateSurgicalCases({
          demandCases: surgicalDemand.demand.total_cases + _civToR2B,
          backlog: previousDayState.surgical_backlog || 0,
          teams: _teams,
          normalCasesPerTeam: NORMAL_CASES_PER_TEAM_PER_DAY,
          destinations: built.destinations
        }),
        destinations: built.destinations
      };
    }
  }
  const _newCases = regulating ? regulating.new_cases_for_r2b : surgicalDemand.demand.total_cases + _civToR2B;
  const _backlogIn = regulating ? regulating.backlog_for_r2b : (previousDayState.surgical_backlog || 0);
  const tempo = SURGICAL_TEMPO_FROM_LOAD
    ? deriveSurgicalTempo({
        demandCases: _newCases,
        backlog: _backlogIn,
        teams: _teams,
        normalCasesPerTeam: NORMAL_CASES_PER_TEAM_PER_DAY,
        mascalLevel: _mascalLevel,
        maxTempo: medicalConfig.surgical_config?.max_tempo,
        state: previousDayState.surgical_tempo_state
      })
    : null;
  const surgicalAnalysis = tempo
    ? calculateSurgicalRequirements({
        triageDistribution,
        dailyCasualties,
        surgicalTeams: _teams,
        hoursIntoShift: 0,
        surgicalBacklog: previousDayState.surgical_backlog || 0,
        mascalLevel: _mascalLevel,
        tempo,
        exactCounts: config.exactCounts === true
      })
    : surgicalDemand;

  // Queue cascade
  const cascadeAnalysis = calculateSurgicalQueueCascade({
    previousDayBacklog: _backlogIn,
    newCases: regulating ? _newCases : surgicalAnalysis.demand.total_cases,
    surgicalTeams: _teams,
    dayOfOperation: day,
    tempo
  });

  // Supply consumption
  const supplyAnalysis = calculateMedicalSupplyConsumption({
    dailyCasualties,
    currentStock: medicalConfig.supply_status || {},
    dailyResupply: medicalConfig.daily_resupply || {}
  });

  // Calculate combined DOW impact from resource constraints
  let resourceDOWModifier = 1.0;
  
  // Blood shortage impact - use imported function from constants
  const bloodDOWImpact = getBloodShortageDOWModifier(bloodAlertLevel);
  resourceDOWModifier *= bloodDOWImpact.modifier;

  // WBB mitigation
  if (wbbAnalysis.needed && wbbAnalysis.impact) {
    resourceDOWModifier *= (1 - wbbAnalysis.impact.dow_reduction_percent / 100);
  }

  // Surgical queue impact
  if (cascadeAnalysis.output.end_of_day_backlog > 0) {
    resourceDOWModifier *= (1 + cascadeAnalysis.mortality_impact.delay_mortality_rate / 100);
  }

  return {
    blood: {
      requirements: bloodAnalysis,
      status: {
        current_stock_prbc: currentBloodStock,
        shortfall: bloodShortfall,
        alert_level: bloodAlertLevel,
        // Add detailed blood DOW impact from constants
        dow_impact: bloodDOWImpact,
        // Alert level thresholds from constants
        alert_thresholds: {
          green: BLOOD_ALERT_LEVELS?.GREEN?.dos_range || { min: 5 },
          yellow: BLOOD_ALERT_LEVELS?.YELLOW?.dos_range || { min: 3, max: 5 },
          red: BLOOD_ALERT_LEVELS?.RED?.dos_range || { min: 1, max: 3 },
          black: BLOOD_ALERT_LEVELS?.BLACK?.dos_range || { min: 0, max: 1 }
        }
      },
      wbb: wbbAnalysis
    },
    
    surgical: {
      requirements: surgicalAnalysis,
      cascade: cascadeAnalysis,
      end_of_day_backlog: cascadeAnalysis.output.end_of_day_backlog,
      tempo,
      tempo_state: tempo ? tempo.state : (previousDayState.surgical_tempo_state || null),
      regulating
    },
    
    supplies: supplyAnalysis,
    
    resource_impact: {
      combined_dow_modifier: Math.round(resourceDOWModifier * 100) / 100,
      additional_dow_percent: Math.round((resourceDOWModifier - 1) * 100),
      critical_shortages: [
        bloodAlertLevel === 'RED' || bloodAlertLevel === 'BLACK' ? 'BLOOD' : null,
        surgicalAnalysis.queue.is_over_capacity ? 'SURGICAL_CAPACITY' : null,
        supplyAnalysis.supply_status.critical ? 'MEDICAL_SUPPLIES' : null
      ].filter(Boolean)
    },
    
    recommendations: [
      ...bloodAnalysis.recommendations || [],
      ...wbbAnalysis.recommendations || [],
      ...surgicalAnalysis.recommendations || []
    ].filter(Boolean)
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  BLOOD_BY_TRIAGE,
  SURGERY_BY_TRIAGE,
  DCS_THRESHOLDS,
  SURGICAL_FATIGUE
};

// Named default export (fixes lint warning)
const resourceModule = {
  // Main calculation functions
  calculateBloodRequirementsDetailed,
  calculateWBBRequirements,
  calculateSurgicalRequirements,
  calculateSurgicalQueueCascade,
  calculateMedicalSupplyConsumption,
  calculateComprehensiveResources,
  
  // Local constants (exported for testing/reference)
  BLOOD_BY_TRIAGE,
  SURGERY_BY_TRIAGE,
  DCS_THRESHOLDS,
  SURGICAL_FATIGUE
};

export default resourceModule;
